import React, { useState } from "react";
import { Link, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import Logo from "@/components/Logo";

export default function ResetPassword() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const location = useLocation();
  const isSetPassword = location.pathname.startsWith("/set-password");
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) { toast.error(t("auth_page.pwd_min_8")); return; }
    if (pwd !== confirm) { toast.error(t("auth_page.pwd_mismatch")); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      toast.success(t("auth_page.pwd_updated"));
      nav("/login", { replace: true });
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-appBg px-4">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Link to="/"><Logo className="h-10" /></Link>
        </div>
        <div className="bg-white rounded-2xl p-8 ring-1 ring-slate-200 shadow-sm">
          <h1 className="font-heading text-2xl font-bold text-[#1B3A6B]">{t(isSetPassword ? "auth_page.set_title" : "auth_page.reset_title", { defaultValue: isSetPassword ? "Set your password" : undefined })}</h1>
          <p className="mt-1 text-sm text-slate-500">{t(isSetPassword ? "auth_page.set_subtitle" : "auth_page.reset_subtitle", { defaultValue: isSetPassword ? "Welcome to Trustline! Pick a password to access your bookings, invoices and referral rewards." : undefined })}</p>
          {!token && (
            <p className="mt-4 text-sm text-rose-600 bg-rose-50 rounded-md p-3">{t("auth_page.reset_no_token")}</p>
          )}
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="pwd">{t("auth_page.new_password")}</Label>
              <Input id="pwd" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} required className="mt-1.5" data-testid="reset-pwd" />
            </div>
            <div>
              <Label htmlFor="confirm">{t("auth_page.confirm_password")}</Label>
              <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="mt-1.5" data-testid="reset-confirm" />
            </div>
            <Button type="submit" disabled={busy || !token} className="w-full bg-[#1B3A6B] hover:bg-[#152D54] text-white rounded-full font-semibold h-11" data-testid="reset-submit">
              {busy ? t("auth_page.updating") : t("auth_page.update_password")}
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-slate-500">
            <Link to="/login" className="text-[#1B3A6B] font-semibold">{t("auth_page.back_to_login")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
